import React, { useCallback, useMemo } from 'react';
import queryString from 'query-string';
import { graphql, useFragment } from 'react-relay';
import { isFavoritableItemTypename } from '@jira/platform__favouriting-favourite-change-provider/src/model/utils.tsx';
import {
	BUTTON,
	DROPDOWN_ITEM,
} from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/action-subject-type.tsx';
import {
	ADD_TO_STARRED,
	REMOVE_FROM_STARRED,
} from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/item-id.tsx';
import type { OnCloseProps } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { FavoriteToggleButton } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/ui/favorite-toggle-button/index.tsx';
import { MoreNavMenuButton } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/ui/more-nav-menu-button/main.tsx';
import getXsrfToken from '@jira/platform__utils-xsrf-token/src/index.tsx';
import { createAri } from '@jira/platform__utils-ari/src/index.tsx';
import { saveFilterDialogEntryPoint } from '@jira/issue-navigator__save-filter-dialog/src/ui/save-filter-dialog/saveFilterDialogEntryPoint.entrypoint.tsx';
import { updateFilterDialogEntryPoint } from '@jira/issue-navigator__save-filter-dialog/src/ui/update-filter-dialog/updateFilterDialogEntryPoint.entrypoint.tsx';
import { ReorderActionsRenderer } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/reorder-actions-renderer/ReorderActionsRenderer.tsx';
import CopyIcon from '@atlaskit/icon/core/copy';
import DeleteIcon from '@atlaskit/icon/core/delete';
import EditIcon from '@atlaskit/icon/core/edit';
import EmailIcon from '@atlaskit/icon/core/email';
import PersonIcon from '@atlaskit/icon/core/person';
import ButtonItem from '@atlaskit/menu/button-item';
import LinkItem from '@atlaskit/menu/link-item';
import Section from '@atlaskit/menu/section';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { useRouterActions } from '@atlassian/react-resource-router';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useActivationId } from '@atlassian/jira-tenant-context-controller/src/components/activation-id/index.tsx';

import { ModalEntryPointPressableTrigger } from '@atlassian/jira-modal-entry-point-pressable-trigger/src/ModalEntryPointPressableTrigger.tsx';
import {
	getFilterActionAttributes,
	getFilterMeatBallAttributes,
	useFilterActionsAnalytics,
} from '../../utils/analytics/index.tsx';
import { useFilterReorderingIds } from '../../utils/use-filter-reordering-id.tsx';
import type { Nav4FilterActionsRefactored$key } from './__generated__/Nav4FilterActionsRefactored.graphql.ts';
import { ChangeOwnerModal } from './change-owner-modal/index.tsx';
import { DeleteFilterModal } from './delete-filter-modal/index.tsx';
import { useModalState } from './utils.tsx';

const manageSubscription = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-filters.common.filter-actions.manage-subscription',

	defaultMessage: 'Manage subscriptions',
	description:
		'Action in a contextual menu that allows the user to manage subscriptions for a Jira filter',
});

const copyFilter = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-filters.common.filter-actions.copy-filter',

	defaultMessage: 'Copy filter',
	description: 'Action in a contextual menu that allows the user to copy a Jira filter',
});

const copyFilterDialogTitle = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-filters.common.filter-actions.copy-filter-dialog-title',

	defaultMessage: 'Copy filter',
	description: 'Title of the dialog that allows the user to copy a Jira filter',
});

const edit = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-filters.common.filter-actions.edit',

	defaultMessage: 'Edit filter',
	description: 'Action in a contextual menu that allows the user to edit a Jira filter',
});

const changeOwner = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-filters.common.filter-actions.change-owner',

	defaultMessage: 'Change owner',
	description: "Action in a contextual menu that allows the user to change a Jira filter's owner",
});

const deleteMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-filters.common.filter-actions.delete',

	defaultMessage: 'Delete filter',
	description: 'Action in a contextual menu that allows the user to move a Jira filter to trash',
});

const manageSubscriptionWithName = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-filters.common.filter-actions.manage-subscription-with-name',

	defaultMessage: 'Manage subscriptions for {name}',
	description:
		'Used as the alt text for the "manage subscription" menu button item. The {name} attribute refers to the name of the filter that the user wants to manage subscriptions for. For example, this could be a filter name like "blocked issues" and the full message would be "Manage Subscriptions for blocked issues".',
});

const copyFilterWithName = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-filters.common.filter-actions.copy-filter-with-name',

	defaultMessage: 'Copy filter {name}',
	description:
		'Used as the alt text for the "copy filter" menu button item. The {name} attribute refers to the name of the filter that the user wants to copy. For example, this could be a filter name like "blocked issues" and the full message would be "Copy filter blocked issues".',
});

const editWithName = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-filters.common.filter-actions.edit-with-name',

	defaultMessage: 'Edit filter {name}',
	description:
		'Used as the alt text for the "edit filter" menu button item. The {name} attribute refers to the name of the filter that the user wants to edit. For example, this could be a filter name like "blocked issues" and the full message would be "Edit filter blocked issues".',
});

const changeOwnerWithName = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-filters.common.filter-actions.change-owner-with-name',

	defaultMessage: 'Change owner for {name}',
	description:
		'Used as the alt text for the "change owner" menu button item. The {name} attribute refers to the name of the filter that the user wants to change owners for. For example, this could be a filter name like "blocked issues" and the full message would be "Change owner for blocked issues".',
});

const deleteWithName = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-filters.common.filter-actions.delete-with-name',

	defaultMessage: 'Delete filter {name}',
	description:
		'Used as the alt text for the "delete filter" menu button item. The {name} attribute refers to the name of the filter that the user wants to delete. For example, this could be a filter name like "blocked issues" and the full message would be "Delete filter blocked issues".',
});

type Nav4FilterActionsRefactoredProps = {
	queryRef: Nav4FilterActionsRefactored$key;
	onRefetch: (options: { onComplete: () => void }) => void;
	currentItems: Array<{ id: string; label: string }>;
	currentOrder: Array<{ menuId: string; visible: boolean }>;
	item: {
		id: string;
		label: string;
	};
};

export const Nav4FilterActionsRefactored = ({
	queryRef,
	onRefetch,
	currentItems,
	currentOrder,
	item,
}: Nav4FilterActionsRefactoredProps): React.JSX.Element => {
	const { __typename, isEditable, isFavourite, name, owner, id, filterId } =
		useFragment<Nav4FilterActionsRefactored$key>(
			graphql`
				fragment Nav4FilterActionsRefactored on JiraFilter {
					__typename
					id
					isFavourite
					name
					filterId
					... on JiraCustomFilter {
						isEditable
						owner {
							accountId
						}
					}
				}
			`,
			queryRef,
		);
	const { formatMessage } = useIntl();
	const accountId = useAccountId();
	const isAdmin = useIsAdmin();
	const sendFilterAnalytics = useFilterActionsAnalytics();

	const {
		isOpen: isChangeOwnerModalOpen,
		onOpen: openChangeOwnerModal,
		onClose: closeChangeOwnerModal,
	} = useModalState(false);

	const {
		isOpen: isDeleteFilterModalOpen,
		onOpen: openDeleteFilterModal,
		onClose: closeDeleteFilterModal,
	} = useModalState(false);

	const { push } = useRouterActions();

	const onFilterSave = useCallback(
		(copiedFilterId: string) => {
			push(`/issues/?filter=${copiedFilterId}`);
		},
		[push],
	);

	const isCustom = __typename === 'JiraCustomFilter';

	const isOwner = owner?.accountId && owner.accountId === accountId;

	const sendFilterActionAnalytics = useCallback(
		(itemId: string) => {
			sendFilterAnalytics(
				DROPDOWN_ITEM,
				getFilterActionAttributes({ itemId, isCustom, isFavourite, filterId }),
			);
		},
		[filterId, isCustom, isFavourite, sendFilterAnalytics],
	);

	const modalProps = useMemo(
		() => ({
			width: 'medium',
			shouldScrollInViewport: true,
		}),
		[],
	);

	const saveFilterEntryPointProps = useMemo(
		() => ({
			filterId,
			onFilterSave,
			title: formatMessage(copyFilterDialogTitle),
		}),
		[filterId, onFilterSave, formatMessage],
	);

	const updateFilterEntryPointProps = useMemo(
		() => ({
			filterId: filterId ?? '',
		}),
		[filterId],
	);

	const cloudId = useCloudId();
	const activationId = useActivationId();
	const entryPointParams = useMemo(
		() => ({
			filterAri:
				filterId !== null && filterId !== undefined
					? createAri({
							resourceOwner: 'jira',
							cloudId,
							resourceType: 'filter',
							resourceId: filterId,
							activationId,
						})
					: undefined,
		}),
		[filterId, cloudId, activationId],
	);

	const { entityAri, ownerAri } = useFilterReorderingIds();

	const reorderActions: ((onClose: () => void) => React.ReactNode) | undefined = useCallback(
		(onClose: () => void) => (
			<ReorderActionsRenderer
				currentItems={currentItems}
				currentOrder={currentOrder}
				entityAri={entityAri}
				ownerAri={ownerAri}
				fireAnalyticsEvent={undefined}
				item={item}
				onClose={onClose}
			/>
		),
		[currentItems, currentOrder, entityAri, item, ownerAri],
	);

	const MenuItems = useCallback(
		({ onClose }: OnCloseProps) => {
			const handleClose = () => onClose?.(null);
			const getManageSubscriptionsUrl = (actionType: string) => {
				const parsed = queryString.stringify({
					atl_token: getXsrfToken(),
					returnUrl: 'ManageFilters.jspa',
					filterId,
				});

				return `/${actionType}!default.jspa?${parsed}`;
			};

			return (
				<>
					<Section isList>
						<FavoriteToggleButton
							entityId={id}
							defaultIsStarred={Boolean(isFavourite)}
							entityTypeName={
								isFavoritableItemTypename(__typename) ? __typename : 'JiraCustomFilter'
							}
							onClick={() =>
								sendFilterActionAnalytics(isFavourite ? REMOVE_FROM_STARRED : ADD_TO_STARRED)
							}
							name={name}
						/>
						{reorderActions && reorderActions(handleClose)}
						{isCustom && (
							<LinkItem
								iconBefore={<EmailIcon label="" spacing="spacious" color={token('color.icon')} />}
								href={getManageSubscriptionsUrl('ViewSubscriptions')}
								aria-label={formatMessage(manageSubscriptionWithName, { name })}
								onClick={() => sendFilterActionAnalytics('manageSubscription')}
							>
								{formatMessage(manageSubscription)}
							</LinkItem>
						)}
						<ModalEntryPointPressableTrigger
							modalProps={modalProps}
							entryPoint={saveFilterDialogEntryPoint}
							entryPointProps={saveFilterEntryPointProps}
							entryPointParams={entryPointParams}
							interactionName="jiraSaveFilterDialog.SaveFilter"
							useInternalModal
						>
							{({ ref }) => (
								<ButtonItem
									onClick={(e) => {
										sendFilterActionAnalytics('copyFilter');
										onClose && onClose(e);
									}}
									iconBefore={<CopyIcon label="" spacing="spacious" color={token('color.icon')} />}
									aria-label={formatMessage(copyFilterWithName, { name })}
									ref={ref}
								>
									{formatMessage(copyFilter)}
								</ButtonItem>
							)}
						</ModalEntryPointPressableTrigger>

						{isCustom && (isOwner || isEditable) && (
							<ModalEntryPointPressableTrigger
								modalProps={modalProps}
								entryPoint={updateFilterDialogEntryPoint}
								entryPointProps={updateFilterEntryPointProps}
								entryPointParams={entryPointParams}
								interactionName="jiraSaveFilterDialog.UpdateFilterDetails"
								useInternalModal
							>
								{({ ref }) => (
									<ButtonItem
										onClick={(e) => {
											sendFilterActionAnalytics('editFilter');
											onClose && onClose(e);
										}}
										iconBefore={
											<EditIcon label="" spacing="spacious" color={token('color.icon')} />
										}
										aria-label={formatMessage(editWithName, { name })}
										ref={ref}
									>
										{formatMessage(edit)}
									</ButtonItem>
								)}
							</ModalEntryPointPressableTrigger>
						)}

						{isCustom && (isOwner || isAdmin) && (
							<ButtonItem
								onClick={(e) => {
									openChangeOwnerModal();
									sendFilterActionAnalytics('changeOwner');
									onClose && onClose(e);
								}}
								iconBefore={<PersonIcon label="" spacing="spacious" color={token('color.icon')} />}
								aria-label={formatMessage(changeOwnerWithName, { name })}
							>
								{formatMessage(changeOwner)}
							</ButtonItem>
						)}
					</Section>
					{isCustom && (isOwner || isAdmin) && (
						<Section hasSeparator>
							<ButtonItem
								onClick={(e) => {
									openDeleteFilterModal();
									sendFilterActionAnalytics('deleteFilter');
									onClose && onClose(e);
								}}
								iconBefore={
									<DeleteIcon label="" color={token('color.icon.danger')} spacing="spacious" />
								}
								aria-label={formatMessage(deleteWithName, { name })}
							>
								<Text color="color.text.danger">{formatMessage(deleteMessage)}</Text>
							</ButtonItem>
						</Section>
					)}
				</>
			);
		},
		[
			id,
			isFavourite,
			__typename,
			name,
			reorderActions,
			isCustom,
			formatMessage,
			modalProps,
			saveFilterEntryPointProps,
			entryPointParams,
			isOwner,
			isEditable,
			updateFilterEntryPointProps,
			isAdmin,
			filterId,
			sendFilterActionAnalytics,
			openChangeOwnerModal,
			openDeleteFilterModal,
		],
	);

	const sendMeatballAnalytics = useCallback(() => {
		sendFilterAnalytics(BUTTON, getFilterMeatBallAttributes({ isCustom, isFavourite, filterId }));
	}, [filterId, isCustom, isFavourite, sendFilterAnalytics]);

	return (
		<>
			<MoreNavMenuButton onOpen={sendMeatballAnalytics} MenuItems={MenuItems} name={name} />

			<ChangeOwnerModal
				isOpen={isChangeOwnerModalOpen}
				filterId={filterId}
				onRefetch={onRefetch}
				onClose={closeChangeOwnerModal}
			/>

			<DeleteFilterModal
				isOpen={isDeleteFilterModalOpen}
				name={name}
				filterId={filterId}
				onRefetch={onRefetch}
				onClose={closeDeleteFilterModal}
			/>
		</>
	);
};
